import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Archive from './pages/Archive';
import PostDetail from './pages/PostDetail';
import WritePost from './pages/WritePost';
import Auth from './pages/Auth';
import UserCenter from './pages/UserCenter';
import NotFound from './pages/NotFound';
import './App.css';

// 页面过渡动画组件
const PageTransition = ({ children }) => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("fadeIn");

  useEffect(() => {
    if (location !== displayLocation) {
      setTransitionStage("fadeOut");
    }
  }, [location, displayLocation]);

  const animationEnd = () => {
    if (transitionStage === "fadeOut") {
      setTransitionStage("fadeIn");
      setDisplayLocation(location);
    }
  };

  return (
    <div
      className={`page-content ${transitionStage}`}
      onAnimationEnd={animationEnd}
    >
      {children}
    </div>
  );
};

// 滚动到顶部组件
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  return null;
};

// 模拟用户登录状态
const PrivateRoute = ({ children }) => {
  const [isAuthenticated] = useState(true); // 这里可以根据实际登录状态修改
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }
  
  return children;
};

function App() {
  const [theme, setTheme] = useState('light');
  const [isLoading, setIsLoading] = useState(true);

  // 模拟加载延迟
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  // 切换主题
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  // 应用主题到body
  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
  }, [theme]);

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-container">
          <div className="cat-loader">
            <div className="cat">
              <div className="ear ear--left"></div>
              <div className="ear ear--right"></div>
              <div className="face">
                <div className="eye eye--left">
                  <div className="eye-pupil"></div>
                </div>
                <div className="eye eye--right">
                  <div className="eye-pupil"></div>
                </div>
                <div className="muzzle"></div>
              </div>
            </div>
          </div>
          <div className="loading-text">加载中...</div>
          <div className="loading-subtext">喵～马上就好</div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className={`App ${theme}`}>
        <ScrollToTop />
        <Header toggleTheme={toggleTheme} theme={theme} />
        <PageTransition>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/archive" element={<Archive />} />
            <Route path="/post/:id" element={<PostDetail />} />
            <Route 
              path="/write" 
              element={
                <PrivateRoute>
                  <WritePost />
                </PrivateRoute>
              } 
            />
            <Route path="/auth" element={<Auth />} />
            <Route 
              path="/user" 
              element={
                <PrivateRoute>
                  <UserCenter />
                </PrivateRoute>
              } 
            />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" />} />
          </Routes>
        </PageTransition>
        <Footer />
        
        {/* 返回顶部按钮 */}
        <button 
          className="back-to-top" 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="返回顶部"
        >
          <span className="cat-paw">🐾</span>
          <span className="arrow-up">↑</span>
        </button>
        
        {/* 主题切换浮动按钮 */}
        <button 
          className="theme-toggle-float" 
          onClick={toggleTheme}
          aria-label={`切换到${theme === 'light' ? '深色' : '浅色'}模式`}
        >
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
      </div>
    </Router>
  );
}

export default App;